import classNames from "classnames";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { TriangleSmallIcon } from "@icons";
import type { SelectedPreviewEntity } from "./applyCoaTemplatePreviewEntries";
import { ApplyCoaPreviewEntityRow } from "./ApplyCoaPreviewEntityRow";
import { ApplyCoaPreviewSelectionCheckbox } from "./ApplyCoaPreviewSelectionCheckbox";
import {
    getClientCheckboxState,
    getClientSelectionDisabledReason,
} from "./applyCoaTemplatePreviewStep.utils";
import styles from "./ApplyCoaTemplatePreviewStep.module.scss";

interface Props {
    search: string | undefined;
    onSearchChange: (value: string | undefined) => void;
    groupedEntries: Array<[string, SelectedPreviewEntity[]]>;
    collapsedClients: ReadonlySet<string>;
    selectedEntityIds: ReadonlySet<number>;
    activeEntityId: number | null;
    appliedEntityIds: ReadonlySet<number>;
    applyingEntityId: number | null;
    disabledEntityIds: ReadonlySet<number>;
    // Per-entity explanation for a disabled control, keyed by entity id. Callers
    // that have no reasons to offer pass an empty map.
    disabledReasonsByEntityId: ReadonlyMap<number, string>;
    revertedEntityIds: ReadonlySet<number>;
    selectionMode: "apply" | "undo" | "readOnly";
    onToggleClientCollapsed: (clientLabel: string) => void;
    onToggleClientSelection: (clientEntries: SelectedPreviewEntity[]) => void;
    onSelectEntity: (entityId: number) => void;
    onToggleEntitySelection: (entityId: number) => void;
}

// Reverted entities show a completed "Reverted" state instead of a checkbox;
// everything else (including atomic-blocked/superseded entities) keeps a normal
// checkbox so it can still be selected in the UI.
const hasEntityCheckbox = ({
    entityId,
    revertedEntityIds,
}: {
    entityId: number;
    revertedEntityIds: ReadonlySet<number>;
}) => !revertedEntityIds.has(entityId);

export const ApplyCoaPreviewEntityList = ({
    search,
    onSearchChange,
    groupedEntries,
    collapsedClients,
    selectedEntityIds,
    activeEntityId,
    appliedEntityIds,
    applyingEntityId,
    disabledEntityIds,
    disabledReasonsByEntityId,
    revertedEntityIds,
    selectionMode,
    onToggleClientCollapsed,
    onToggleClientSelection,
    onSelectEntity,
    onToggleEntitySelection,
}: Props) => (
    <div className={styles.listContainer}>
        <div className={styles.listSearch}>
            <SearchFilter
                filter={search}
                onChange={onSearchChange}
                placeholder="Search..."
                minLength={2}
                containerClassName={styles.listSearchInput}
            />
        </div>
        {groupedEntries.map(([clientLabel, clientEntries]) => {
            const isCollapsed = collapsedClients.has(clientLabel);
            const selectableClientEntries = clientEntries.filter(
                (entry) =>
                    hasEntityCheckbox({
                        entityId: entry.entityId,
                        revertedEntityIds,
                    }) && !disabledEntityIds.has(entry.entityId),
            );
            const clientCheckboxState = getClientCheckboxState({
                clientEntries: selectableClientEntries,
                selectedEntityIds,
            });
            // A client whose every entity is already reverted has nothing left to
            // check, so it drops its checkbox the same way those rows do.
            const hasClientCheckbox = clientEntries.some((entry) =>
                hasEntityCheckbox({
                    entityId: entry.entityId,
                    revertedEntityIds,
                }),
            );
            const isClientSelectionDisabled =
                selectableClientEntries.length === 0;
            const clientAriaChecked =
                clientCheckboxState === "indeterminate"
                    ? "mixed"
                    : clientCheckboxState === "checked";
            return (
                // The client name is what disambiguates two entities that share a
                // display name, so each client is a named group rather than a bare
                // wrapper. Presentation is unchanged: the role and label carry no
                // styles.
                <div key={clientLabel} role="group" aria-label={clientLabel}>
                    <div className={styles.clientRow}>
                        {selectionMode !== "readOnly" && hasClientCheckbox && (
                            <ApplyCoaPreviewSelectionCheckbox
                                label={`Select all entities in ${clientLabel}`}
                                ariaChecked={clientAriaChecked}
                                isDisabled={isClientSelectionDisabled}
                                disabledReason={
                                    isClientSelectionDisabled
                                        ? getClientSelectionDisabledReason({
                                              clientEntries,
                                              disabledReasonsByEntityId,
                                          })
                                        : undefined
                                }
                                onClick={() =>
                                    onToggleClientSelection(
                                        selectableClientEntries,
                                    )
                                }
                            >
                                <CheckboxIndicator
                                    state={clientCheckboxState}
                                    disabled={isClientSelectionDisabled}
                                />
                            </ApplyCoaPreviewSelectionCheckbox>
                        )}
                        <button
                            type="button"
                            className={styles.clientCollapseButton}
                            aria-expanded={!isCollapsed}
                            onClick={() => onToggleClientCollapsed(clientLabel)}
                        >
                            <TriangleSmallIcon
                                className={classNames(styles.chevron, {
                                    [styles.chevronCollapsed]: isCollapsed,
                                })}
                            />
                            <span className={styles.clientLabel}>
                                {clientLabel}
                            </span>
                        </button>
                    </div>
                    {!isCollapsed &&
                        clientEntries.map((entry) => (
                            <ApplyCoaPreviewEntityRow
                                key={entry.entityId}
                                entry={entry}
                                isActive={entry.entityId === activeEntityId}
                                isEntitySelected={selectedEntityIds.has(
                                    entry.entityId,
                                )}
                                isApplied={appliedEntityIds.has(entry.entityId)}
                                isApplying={applyingEntityId === entry.entityId}
                                isDisabled={disabledEntityIds.has(
                                    entry.entityId,
                                )}
                                isReverted={revertedEntityIds.has(
                                    entry.entityId,
                                )}
                                hasCheckbox={hasEntityCheckbox({
                                    entityId: entry.entityId,
                                    revertedEntityIds,
                                })}
                                disabledReason={disabledReasonsByEntityId.get(
                                    entry.entityId,
                                )}
                                selectionMode={selectionMode}
                                onSelect={onSelectEntity}
                                onToggleSelection={onToggleEntitySelection}
                            />
                        ))}
                </div>
            );
        })}
    </div>
);
